import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';
import { getMapboxAccessToken } from '../../util/loadMapbox';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const GEOCODING_API_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

// Same formula as mapbox-gl's LngLat#toBounds(radius), which this used to call — the library is
// no longer loaded just for location search.
const EARTH_CIRCUMFERENCE_AT_EQUATOR_METERS = 40075017;

const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const latAccuracy = (360 * distance) / EARTH_CIRCUMFERENCE_AT_EQUATOR_METERS;
  const lngAccuracy = latAccuracy / Math.cos((Math.PI / 180) * latlng.lat);
  return new SDKLatLngBounds(
    new SDKLatLng(latlng.lat + latAccuracy, latlng.lng + lngAccuracy),
    new SDKLatLng(latlng.lat - latAccuracy, latlng.lng - lngAccuracy)
  );
};

// Query serialization as in @mapbox/mapbox-sdk (urlUtils.appendQueryObject): array items without
// null/undefined joined by ',', then the whole value URI-encoded; an empty value is sent as a bare
// key, undefined/null/false are left out.
const appendQueryParam = (parts, key, value) => {
  if (value === undefined || value === null || value === false) {
    return;
  }
  const flat = Array.isArray(value) ? value.filter(v => v != null).join(',') : value;
  parts.push(
    flat === '' || flat === true
      ? encodeURIComponent(key)
      : `${encodeURIComponent(key)}=${encodeURIComponent(String(flat))}`
  );
};

/**
 * Forward geocoding request, the same URL @mapbox/mapbox-sdk's
 * `geocoding.forwardGeocode({...}).send()` built: GET /geocoding/v5/mapbox.places/{query}.json.
 * Plain fetch, so location search needs neither mapbox-gl.js nor the 68 KB SDK bundle.
 */
const forwardGeocode = (accessToken, { query, ...params }) => {
  const parts = [];
  Object.entries(params).forEach(([key, value]) => appendQueryParam(parts, key, value));
  appendQueryParam(parts, 'access_token', accessToken);
  const url = `${GEOCODING_API_URL}/${encodeURIComponent(query)}.json?${parts.join('&')}`;
  return fetch(url).then(response => {
    if (!response.ok) {
      throw new Error(`Mapbox geocoding failed with status ${response.status}`);
    }
    return response.json();
  });
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  getAccessToken() {
    const accessToken = getMapboxAccessToken();
    if (!accessToken) {
      throw new Error('Mapbox access token is not yet available');
    }
    return accessToken;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, countryLimit, locale) {
    const limitCountriesMaybe = countryLimit ? { country: countryLimit } : {};

    return Promise.resolve()
      .then(() =>
        forwardGeocode(this.getAccessToken(), {
          query: search,
          limit: 5,
          ...limitCountriesMaybe,
          language: [locale],
        })
      )
      .then(body => {
        return {
          search,
          predictions: body.features,
        };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
    });
  }
}

export default GeocoderMapbox;
